import type {
  EnhancedDeal,
  FabricationMethod,
  RfqPackage,
  RfqProject,
  RfqProjectStatus,
  RfqSubmission,
  TimberProject,
  VendorWorkType,
} from '../types';
import { compareTimberProjectsByDealMap } from './projectNumberSort';
import { parseLegacyTitle } from './projectName';
import { rfqProjectSupplierOrgIds } from './rfqProjectSuppliers';
import { normalizeWorkType } from './vendorHelpers';
import { timberQuoteStreamKey } from './timberQuoteStreamKey';

export type SupplierDisplayStatus =
  | 'published'
  | 'invited'
  | 'bid_submitted'
  | 'bid_declined'
  | 'approved'
  | 'rejected';

export type SupplierInviteEntry = {
  orgId: string;
  orgName: string;
  hasQuote: boolean;
  isApproved: boolean;
  status?: RfqProjectStatus;
  displayStatus: SupplierDisplayStatus | null;
};

export type WorkTypeVendorSummary = {
  rfqProjectId?: string;
  /** @deprecated Use {@link suppliers} — kept for compatibility */
  invitedOrgNames: string[];
  suppliers: SupplierInviteEntry[];
  approvedOrgId?: string;
  packageCount: number;
  quoteCount: number;
  status?: RfqProjectStatus;
  hasWorkOrder: boolean;
};

export type VendorDealRow = {
  dealId: string;
  timberProjectId?: string;
  numberDisplay: string;
  descriptor: string;
  clientLabel: string;
  location?: string;
  fabricationMethod?: FabricationMethod;
  phase?: TimberProject['phase'];
  status?: TimberProject['status'];
  timber: WorkTypeVendorSummary;
  cnc: WorkTypeVendorSummary;
};

type OrgLookup = Map<string, { name?: string }>;

function orgName(lookup: OrgLookup, id: string): string {
  return lookup.get(id)?.name?.trim() || id;
}

const STATUS_RANK: Record<RfqProjectStatus, number> = {
  closed: 4,
  quoted: 3,
  active: 2,
  invited: 1,
};

function pickBestStatus(statuses: RfqProjectStatus[]): RfqProjectStatus | undefined {
  if (!statuses.length) return undefined;
  return statuses.slice().sort((a, b) => STATUS_RANK[b] - STATUS_RANK[a])[0];
}

function resolveApprovedOrgId(projects: RfqProject[]): string | undefined {
  for (const p of projects) {
    if (p.approvedSupplierOrgId) return p.approvedSupplierOrgId;
  }
  const closed = projects.find((p) => p.status === 'closed' && p.supplierOrgId);
  return closed?.supplierOrgId;
}

export function supplierDisplayStatusLabel(status: SupplierDisplayStatus): string {
  switch (status) {
    case 'approved':
      return 'Approved';
    case 'bid_submitted':
      return 'Bid Submitted';
    case 'bid_declined':
      return 'Bid Declined';
    case 'rejected':
      return 'Rejected';
    case 'published':
      return 'Published';
    default:
      return 'Invited';
  }
}

function deriveSupplierDisplayStatus(
  orgId: string,
  approvedOrgId: string | undefined,
  quotesByOrg: Set<string>,
  declinedByOrg: Set<string>,
  publishedByOrg: Set<string>,
  projectClosed: boolean,
): SupplierDisplayStatus | null {
  if (approvedOrgId === orgId) return 'approved';
  const hasQuote = quotesByOrg.has(orgId);
  const declined = declinedByOrg.has(orgId);
  if (declined && !hasQuote) return 'bid_declined';
  if (hasQuote && projectClosed && approvedOrgId && approvedOrgId !== orgId) return 'rejected';
  if (hasQuote) return 'bid_submitted';
  if (publishedByOrg.has(orgId)) return 'published';
  return null;
}

export function buildWorkTypeSummary(
  dealId: string,
  workType: VendorWorkType,
  rfqProjects: RfqProject[],
  rfqPackages: RfqPackage[],
  rfqSubmissions: RfqSubmission[],
  supplierOrgById: OrgLookup,
): WorkTypeVendorSummary {
  const projects = rfqProjects.filter(
    (p) => p.dealId === dealId && normalizeWorkType(p.workType) === workType,
  );
  const orgIds = new Set<string>();
  const orgToStatuses = new Map<string, RfqProjectStatus[]>();

  for (const p of projects) {
    for (const id of rfqProjectSupplierOrgIds(p)) {
      orgIds.add(id);
      const list = orgToStatuses.get(id) ?? [];
      list.push(p.status);
      orgToStatuses.set(id, list);
    }
  }

  const projectIds = new Set(projects.map((p) => p.id).filter(Boolean) as string[]);
  const pkgs = rfqPackages.filter((p) => p.rfqProjectId && projectIds.has(p.rfqProjectId));
  const quotes = rfqSubmissions.filter(
    (s) => s.rfqProjectId && projectIds.has(s.rfqProjectId) && !s.notBidding,
  );
  // Revisions of the same quote stream (species + structure set) count once.
  const quoteStreamKeys = new Set(
    quotes.map((s) => `${s.rfqPackageId}|${timberQuoteStreamKey(s)}`),
  );
  const quotesByOrg = new Set(quotes.map((s) => s.supplierOrgId).filter(Boolean) as string[]);
  const declinedByOrg = new Set(
    rfqSubmissions
      .filter((s) => s.rfqProjectId && projectIds.has(s.rfqProjectId) && s.notBidding && s.supplierOrgId)
      .map((s) => s.supplierOrgId as string),
  );
  const publishedByOrg = new Set(pkgs.map((p) => p.supplierOrgId).filter(Boolean) as string[]);
  const projectClosed = projects.some((p) => p.status === 'closed');

  const primary = projects
    .slice()
    .sort((a, b) => {
      const ta = a.updatedAt instanceof Date ? a.updatedAt.getTime() : a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
      const tb = b.updatedAt instanceof Date ? b.updatedAt.getTime() : b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
      return tb - ta;
    })[0];

  const approvedOrgId = resolveApprovedOrgId(projects);

  const suppliers: SupplierInviteEntry[] = Array.from(orgIds)
    .map((orgId) => ({
      orgId,
      orgName: orgName(supplierOrgById, orgId),
      hasQuote: quotesByOrg.has(orgId),
      isApproved: approvedOrgId === orgId,
      status: pickBestStatus(orgToStatuses.get(orgId) ?? []),
      displayStatus: deriveSupplierDisplayStatus(
        orgId,
        approvedOrgId,
        quotesByOrg,
        declinedByOrg,
        publishedByOrg,
        projectClosed,
      ),
    }))
    .sort((a, b) => {
      if (a.isApproved !== b.isApproved) return a.isApproved ? -1 : 1;
      if (a.hasQuote !== b.hasQuote) return a.hasQuote ? -1 : 1;
      return a.orgName.localeCompare(b.orgName);
    });

  return {
    rfqProjectId: primary?.id,
    invitedOrgNames: suppliers.map((s) => s.orgName),
    suppliers,
    approvedOrgId,
    packageCount: pkgs.length,
    quoteCount: quoteStreamKeys.size,
    status: primary?.status,
    hasWorkOrder: projects.length > 0,
  };
}

export function selectRfqProjectForDeal(
  dealId: string,
  workType: VendorWorkType,
  rfqProjects: RfqProject[],
): RfqProject | null {
  const matches = rfqProjects.filter(
    (p) => p.dealId === dealId && normalizeWorkType(p.workType) === workType,
  );
  return (
    matches
      .slice()
      .sort((a, b) => {
        const ta = a.updatedAt instanceof Date ? a.updatedAt.getTime() : a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
        const tb = b.updatedAt instanceof Date ? b.updatedAt.getTime() : b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
        return tb - ta;
      })[0] || null
  );
}

export function timberProjectToVendorRow(
  p: TimberProject,
  deal: EnhancedDeal | undefined,
  rfqProjects: RfqProject[],
  rfqPackages: RfqPackage[],
  rfqSubmissions: RfqSubmission[],
  supplierOrgById: OrgLookup,
): VendorDealRow | null {
  if (!p.dealId) return null;
  const num = (deal?.projectNumber || '').trim();
  const numberDisplay = num ? (num.startsWith('#') ? num : `#${num}`) : '';
  const descriptor = (
    (deal?.descriptor || '').trim()
    || parseLegacyTitle(deal?.title || '').descriptor
    || p.name
    || ''
  ).trim();
  const clientLabel = (deal?.clientLabel || p.clientName || deal?.contactName || '').trim();

  return {
    dealId: p.dealId,
    timberProjectId: p.id,
    numberDisplay,
    descriptor,
    clientLabel,
    location: p.location,
    fabricationMethod: p.fabricationMethod,
    phase: p.phase,
    status: p.status,
    timber: buildWorkTypeSummary(p.dealId, 'timber_rfq', rfqProjects, rfqPackages, rfqSubmissions, supplierOrgById),
    cnc: buildWorkTypeSummary(p.dealId, 'cnc_quote', rfqProjects, rfqPackages, rfqSubmissions, supplierOrgById),
  };
}

export function sortTimberProjectsForVendorAdmin(
  projects: TimberProject[],
  dealsById: Map<string, EnhancedDeal>,
): TimberProject[] {
  return projects.slice().sort((a, b) => compareTimberProjectsByDealMap(a, b, dealsById));
}

export function formatVendorSummaryLine(summary: WorkTypeVendorSummary): string {
  if (!summary.hasWorkOrder) return 'Not started';
  if (!summary.suppliers.length) return 'No vendors invited';

  const parts = summary.suppliers.map((s) => {
    const tags: string[] = [s.orgName];
    if (s.isApproved) tags.push('approved');
    else if (s.hasQuote) tags.push('quoted');
    return tags.join(' · ');
  });

  const meta: string[] = [];
  if (summary.packageCount > 0) {
    meta.push(`${summary.packageCount} pkg${summary.packageCount === 1 ? '' : 's'}`);
  }
  if (summary.quoteCount > 0) {
    meta.push(`${summary.quoteCount} quote${summary.quoteCount === 1 ? '' : 's'}`);
  }

  return meta.length ? `${parts.join('; ')} (${meta.join(', ')})` : parts.join('; ');
}
